import { safeString } from './lectureNormalization';
import { htmlToText, generateId } from './contentUtils';
import { normalizeBackgroundMapForPersistence, normalizeBackgroundPersistenceValue } from './presetTemplates';

export const STABLE_FORMAT = 'boppps_lesson';
export const STABLE_SCHEMA_VERSION = '1.0.0';
export const APP_VERSION = '5.0.0';

const APP_EXPORT_VERSION = '2026-01';

const SECTION_ID_TO_STABLE = {
  'bridge-in': 'bridge_in',
  outcomes: 'objectives',
  'learning-outcomes': 'objectives',
  objectives: 'objectives',
  'pre-assessment': 'pre_assessment',
  'participatory-learning': 'participatory_learning',
  'post-assessment': 'post_assessment',
  summary: 'summary'
};

const STABLE_TO_SECTION_ID = Object.entries(SECTION_ID_TO_STABLE).reduce((acc, [legacy, stable]) => {
  if (!acc[stable]) acc[stable] = legacy;
  return acc;
}, {});

const buildEmptySection = () => ({ text: '', bullets: [], artifacts: [], items: [], activities: [] });

export const emptySectionsObject = () => ({
  bridge_in: buildEmptySection(),
  objectives: buildEmptySection(),
  pre_assessment: buildEmptySection(),
  participatory_learning: buildEmptySection(),
  post_assessment: buildEmptySection(),
  summary: buildEmptySection()
});

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null) return [];
  return [value];
};

export const normalizeSectionShape = (section) => {
  const base = buildEmptySection();
  if (!section || typeof section !== 'object') return base;
  const normalized = { ...base, ...section };
  normalized.text = safeString(normalized.text);
  normalized.bullets = normalizeArray(normalized.bullets).map((b) => safeString(b));
  normalized.artifacts = normalizeArray(normalized.artifacts).map((a) =>
    typeof a === 'object' && a !== null ? a : { title: safeString(a) }
  );
  normalized.items = normalizeArray(normalized.items).map((i) => safeString(i));
  normalized.activities = normalizeArray(normalized.activities);
  return normalized;
};

const canonicalContext = (context = {}) => ({
  course: safeString(context.course),
  unit: safeString(context.unit),
  grade_level: safeString(context.grade_level),
  duration_minutes: Number.isFinite(context.duration_minutes) ? context.duration_minutes : null,
  delivery_mode: safeString(context.delivery_mode),
  standards: normalizeArray(context.standards).map((s) => safeString(s)),
  materials: normalizeArray(context.materials).map((m) => safeString(m))
});

const slugify = (value) => {
  const safe = safeString(value, 'lesson');
  const slug = safe
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug || 'lesson';
};

const summarizeBlocks = (blocks = []) => {
  const textFragments = [];
  blocks.forEach((block) => {
    if (!block || block.type === 'divider') return;

    if (typeof block?.content === 'string') {
      textFragments.push(htmlToText(block.content));
      return;
    }

    if (typeof block?.data?.content === 'string') {
      textFragments.push(htmlToText(block.data.content));
      return;
    }

    if (typeof block?.prompt === 'string') {
      textFragments.push(htmlToText(block.prompt));
    }
  });
  return textFragments.join('\n\n').trim();
};

export const buildStableLessonJson = ({
  lessonId,
  createdAt,
  updatedAt,
  title,
  description = '',
  tags = [],
  context = {},
  sections = [],
  rawSections,
  slideVisibility = {},
  slideNames = {},
  slideBackgrounds = {},
  titleCardBackground = null,
  slideDeckName = '',
  bodyLayout = 'slides',
  titleCardSettings = {},
  endTitleCardSettings = {},
  slideHeaderMode = 'single',
  slideHeaderColor = '#7c3aed',
  slideHeaderColors = {},
  slideGlobalCss = '',
  exportVersion,
  extensions = {}
}) => {
  const persistentSlideBackgrounds = normalizeBackgroundMapForPersistence(slideBackgrounds);
  const persistentTitleCardBackground = normalizeBackgroundPersistenceValue(titleCardBackground, { allowEmpty: false });

  const stableSections = emptySectionsObject();
  sections.forEach((section) => {
    const stableKey = SECTION_ID_TO_STABLE[section.id] || SECTION_ID_TO_STABLE[section.type];
    if (!stableKey) return;
    stableSections[stableKey] = normalizeSectionShape({
      title: safeString(section.title),
      text: summarizeBlocks(section.blocks),
      items: section.blocks?.filter((b) => b?.type === 'list')?.flatMap((b) => normalizeArray(b.items || b.content)) || [],
      activities: section.blocks || []
    });
  });

  const flattenedBlocks = sections.flatMap((section) => {
    const stableKey = SECTION_ID_TO_STABLE[section.id] || SECTION_ID_TO_STABLE[section.type];
    if (!stableKey) return [];
    return (section.blocks || []).map((block) => {
      const { id, type, title: blockTitle, content, ...rest } = block || {};
      const data = { ...rest };
      if (content !== undefined) data.content = content;
      return {
        id: safeString(id, generateId()),
        type: safeString(type, 'text'),
        section: stableKey,
        title: safeString(blockTitle),
        data,
        metadata: { source: 'boppps_planner', version: exportVersion ?? 1 }
      };
    });
  });

  const stableLesson = {
    format: STABLE_FORMAT,
    schema_version: STABLE_SCHEMA_VERSION,
    producer: {
      app_name: 'LessonPlanner One',
      app_version: APP_VERSION,
      exported_at: new Date().toISOString()
    },
    id: safeString(lessonId, `lesson-${slugify(title)}`),
    created_at: safeString(createdAt, new Date().toISOString()),
    updated_at: safeString(updatedAt, new Date().toISOString()),
    title: safeString(title),
    description: safeString(description),
    tags: normalizeArray(tags).map((t) => safeString(t)),
    context: canonicalContext(context),
    sections: Object.keys(stableSections).reduce((ordered, key) => {
      ordered[key] = normalizeSectionShape(stableSections[key]);
      return ordered;
    }, {}),
    blocks: flattenedBlocks,
    extensions: {
      boppps_planner: {
        app_export_version: APP_EXPORT_VERSION,
        content_blocks: rawSections || sections,
        functions: extensions.functions || {},
        ui_state: {
          slideVisibility,
          slideNames,
          slideBackgrounds: persistentSlideBackgrounds,
          titleCardBackground: persistentTitleCardBackground,
          slideDeckName,
          bodyLayout,
          titleCardSettings,
          endTitleCardSettings,
          slideHeaderMode,
          slideHeaderColor,
          slideHeaderColors,
          slideGlobalCss
        },
        ...extensions.boppps_planner
      },
      ...extensions
    }
  };

  return { stableLesson, filenameSlug: slugify(title || slideDeckName || 'lesson') };
};

export const validateStableLessonJson = (data) => {
  const errors = [];
  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Payload is not an object'] };
  }
  if (data.format !== STABLE_FORMAT) errors.push('format must be "boppps_lesson"');
  if (data.schema_version !== STABLE_SCHEMA_VERSION) errors.push('schema_version must be "1.0.0"');
  if (!data.sections || typeof data.sections !== 'object') errors.push('sections must be an object');
  ['bridge_in', 'objectives', 'pre_assessment', 'participatory_learning', 'post_assessment', 'summary'].forEach((key) => {
    if (!(key in (data.sections || {}))) errors.push(`sections.${key} missing`);
  });
  if (!Array.isArray(data.blocks)) errors.push('blocks must be an array');
  return { valid: errors.length === 0, errors };
};

export const migrateStableLessonJson = (data) => {
  const migrated = { ...(data || {}) };
  const issues = [];
  if (!migrated.schema_version) {
    migrated.schema_version = STABLE_SCHEMA_VERSION;
    issues.push('schema_version missing; assumed 1.0.0');
  }
  if (!migrated.format) {
    migrated.format = STABLE_FORMAT;
    issues.push('format missing; assumed boppps_lesson');
  }
  migrated.sections = migrated.sections && typeof migrated.sections === 'object' ? { ...migrated.sections } : emptySectionsObject();
  const ensuredSections = emptySectionsObject();
  Object.keys(ensuredSections).forEach((key) => {
    if (!(key in migrated.sections)) issues.push(`added missing section ${key}`);
    ensuredSections[key] = normalizeSectionShape(migrated.sections[key]);
  });
  migrated.sections = ensuredSections;

  if (typeof migrated.objectives === 'string') {
    migrated.sections.objectives = normalizeSectionShape({ items: [migrated.objectives] });
    issues.push('objectives string promoted to array');
  }
  ['bridge_in', 'objectives', 'pre_assessment', 'participatory_learning', 'post_assessment', 'summary'].forEach((key) => {
    if (typeof migrated.sections[key]?.bullets === 'string') {
      migrated.sections[key].bullets = [migrated.sections[key].bullets];
      issues.push(`bullets for ${key} converted to array`);
    }
  });

  if (!migrated.producer || typeof migrated.producer !== 'object') {
    migrated.producer = { app_name: 'LessonPlanner One', app_version: APP_VERSION, exported_at: new Date().toISOString() };
    issues.push('producer missing; added default metadata');
  }

  if (!Array.isArray(migrated.blocks)) migrated.blocks = [];

  return { migrated, issues };
};

const reviveBlockToInternal = (block, generateIdFn = generateId) => {
  const internal = { ...block.data };
  internal.id = safeString(block.id, generateIdFn());
  internal.type = safeString(block.type, 'text');
  if (block.title) internal.title = safeString(block.title);
  if (block.data && block.data.content !== undefined) {
    internal.content = block.data.content;
  }
  return internal;
};

export const stableLessonToInternal = (data, { generateIdFn = generateId } = {}) => {
  const sectionsMap = {};
  if (data?.extensions?.boppps_planner?.content_blocks) {
    data.extensions.boppps_planner.content_blocks.forEach((section) => {
      const stableKey = SECTION_ID_TO_STABLE[section.id] || SECTION_ID_TO_STABLE[section.type];
      const internalId = section.id || STABLE_TO_SECTION_ID[stableKey] || generateIdFn();
      sectionsMap[internalId] = {
        ...section,
        id: internalId,
        blocks: (section.blocks || []).map((blk) => reviveBlockToInternal(blk, generateIdFn))
      };
    });
  }

  (data?.blocks || []).forEach((block) => {
    const stableKey = block.section;
    const internalId = STABLE_TO_SECTION_ID[stableKey] || generateIdFn();
    if (!sectionsMap[internalId]) {
      sectionsMap[internalId] = {
        id: internalId,
        title: safeString(internalId),
        type: 'boppps',
        blocks: []
      };
    }
    sectionsMap[internalId].blocks.push(reviveBlockToInternal(block, generateIdFn));
  });

  const sections = Object.values(sectionsMap);

  const headerData = {
    courseTopic: safeString(data.title || data.context?.course, 'Course Topic - Week 1'),
    instructorName: safeString(data.producer?.app_name ? `${data.producer.app_name} export` : ''),
    instructorEmail: '',
    footerCourseInfo: safeString(data.context?.course),
    footerInstitution: '',
    footerCopyright: '',
    showHeaderBar: true
  };

  const uiState = data?.extensions?.boppps_planner?.ui_state || {};

  return {
    ...headerData,
    week: safeString(data.context?.unit || ''),
    date: '',
    sections,
    bodyLayout: uiState.bodyLayout || 'slides',
    slideBackgrounds: normalizeBackgroundMapForPersistence(uiState.slideBackgrounds),
    titleCardBackground: normalizeBackgroundPersistenceValue(uiState.titleCardBackground, { allowEmpty: false }),
    slideHeaderMode: uiState.slideHeaderMode || 'single',
    slideHeaderColor: uiState.slideHeaderColor || '#7c3aed',
    slideHeaderColors: uiState.slideHeaderColors || {},
    slideGlobalCss: uiState.slideGlobalCss || '',
    titleCardSettings: uiState.titleCardSettings || {},
    endTitleCardSettings: uiState.endTitleCardSettings || {},
    slideVisibility: uiState.slideVisibility || {},
    acknowledgedBlankSlides: uiState.acknowledgedBlankSlides || {},
    slideNames: uiState.slideNames || {},
    slideDeckName: uiState.slideDeckName || safeString(data.title || 'Slide Deck'),
    exportFileName: '',
    htmlExportTitle: '',
    exportVersion: data.extensions?.boppps_planner?.app_export_version || 1
  };
};

export const stableLessonFilename = (slugSource) => {
  const base = slugify(slugSource || 'lesson');
  return `${base}__boppps_lesson__schema-${STABLE_SCHEMA_VERSION}.json`;
};
